import { useEffect, useState } from "react";
import { BarChart3, Table2 } from "lucide-react";
import { api } from "@/lib/api";
import { Card } from "@/components/Kpi";
import { ComparisonChart } from "@/components/Charts";
import { num, MESI, dateIt } from "@/lib/format";

const pct = (v) => (v === null || v === undefined ? "–" : `${v.toLocaleString("it-IT", { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`);
const meseLabel = (m) => MESI[parseInt(m.slice(5), 10) - 1].slice(0, 3);

/** Chiamate in ingresso dal centralino, per filiale e mese: ricevute, risposte e % di risposta. */
export function ChiamateIngresso({ anno = 2026, agenzia = "", testId = "corto-chiamate-ingresso" }) {
  const [dati, setDati] = useState(null);
  const [vista, setVista] = useState("grafico");

  useEffect(() => {
    api.get(`/corto/chiamate-ingresso?anno=${anno}${agenzia ? `&agenzia=${encodeURIComponent(agenzia)}` : ""}`)
      .then((r) => setDati(r.data)).catch(() => setDati({ filiali: [], mesi: [], totale: {} }));
  }, [anno, agenzia]);

  if (!dati) return null;
  const { filiali, mesi, totale: tot } = dati;
  const vuoto = !filiali.length;
  const serie = agenzia
    ? mesi.map((m) => ({ label: meseLabel(m), valore: tot.mesi?.[m]?.perc ?? 0, ricevute: tot.mesi?.[m]?.ricevute, risposte: tot.mesi?.[m]?.risposte, senza_esito: tot.mesi?.[m]?.senza_esito }))
    : [...filiali.map((f) => ({ label: f.agenzia, valore: f.perc ?? 0, ricevute: f.ricevute, risposte: f.risposte, senza_esito: f.senza_esito })),
      { label: "TOTALE", valore: tot.perc ?? 0, ricevute: tot.ricevute, risposte: tot.risposte, senza_esito: tot.senza_esito }];
  return (
    <Card className="p-0 overflow-hidden" data-testid={testId}>
      <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">Chiamate in ingresso · centralino</h3>
          <div className="text-xs text-slate-400 mt-0.5" data-testid="corto-chiamate-ingresso-sub">
            {anno} · {num(tot.ricevute || 0)} ricevute · {num(tot.risposte || 0)} risposte · <span className="text-[#0018A8] font-semibold">{pct(tot.perc)}</span> risposta
            {tot.senza_esito ? ` · ${num(tot.senza_esito)} senza esito registrato (escluse dalla %)` : ""}
            {dati.sync?.ultimo_giorno ? ` · telefonate CRM lette fino al ${dateIt(dati.sync.ultimo_giorno)}` : ""}
          </div>
        </div>
        <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs" data-testid="corto-chiamate-ingresso-vista">
          {[["grafico", BarChart3, "Grafico"], ["tabella", Table2, "Tabella"]].map(([v, Icon, l]) => (
            <button key={v} type="button" onClick={() => setVista(v)} data-testid={`corto-chiamate-ingresso-vista-${v}`}
              className={`inline-flex items-center gap-1 px-3 py-1.5 transition-colors duration-200 ${vista === v ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              <Icon size={13} /> {l}
            </button>
          ))}
        </div>
      </div>
      {vuoto ? (
        <div className="px-4 py-6 text-sm text-slate-400" data-testid="corto-chiamate-ingresso-vuoto">
          {dati.sync?.in_corso ? "Lettura delle telefonate dal CRM in corso…" : "Nessuna chiamata in ingresso letta dal CRM."}
        </div>
      ) : vista === "grafico" ? (
        <div className="p-4">
          <ComparisonChart testId="corto-chiamate-ingresso-grafico" title={agenzia ? `% risposta per mese · ${agenzia}` : "% risposta alle chiamate in ingresso per filiale"}
            data={serie} orizzontale={!agenzia} labelFormat="perc" larghezzaEtichette={agenzia ? 34 : 130}
            height={agenzia ? 260 : Math.max(220, 28 * serie.length + 40)} keys={[{ key: "valore", name: "% risposta", color: "#0018A8" }]}
            tooltipExtra={(p) => (p ? `${num(p.risposte)}/${num((p.ricevute || 0) - (p.senza_esito || 0))}` : null)} />
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="corto-chiamate-ingresso-tabella">
            <thead>
              <tr className="text-left uppercase tracking-[0.12em] text-slate-500 border-b border-slate-200">
                <th className="px-3 py-2">Filiale</th>
                {mesi.map((m) => <th key={m} className="px-2 py-2 text-center whitespace-nowrap">{meseLabel(m)}</th>)}
                <th className="px-3 py-2 text-right">Ricevute</th><th className="px-3 py-2 text-right">Risposte</th><th className="px-3 py-2 text-right">Senza esito</th><th className="px-3 py-2 text-right">% risposta</th>
              </tr>
            </thead>
            <tbody>
              {[...filiali, tot].map((f) => (
                <tr key={f.agenzia} className={`border-b border-slate-100 ${f.agenzia === "TOTALE" ? "font-bold border-t-2 border-slate-300" : "hover:bg-slate-50/70"}`} data-testid={`corto-chiamate-ingresso-riga-${f.agenzia.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  <td className="px-3 py-2 font-medium text-slate-900 whitespace-nowrap">{f.agenzia}</td>
                  {mesi.map((m) => {
                    const c = f.mesi?.[m];
                    return (
                      <td key={m} className="px-2 py-2 text-center font-mono" title={c ? `${num(c.risposte)} risposte su ${num(c.ricevute)}` : ""}>
                        {c ? <><div className="text-[#0018A8] font-semibold">{pct(c.perc)}</div><div className="text-[10px] text-slate-400">{num(c.risposte)}/{num(c.ricevute)}</div></> : <span className="text-slate-300">–</span>}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-right font-mono">{num(f.ricevute)}</td>
                  <td className="px-3 py-2 text-right font-mono">{num(f.risposte)}</td>
                  <td className="px-3 py-2 text-right font-mono text-slate-400">{num(f.senza_esito || 0)}</td>
                  <td className="px-3 py-2 text-right font-mono font-semibold text-[#0018A8]">{pct(f.perc)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
